import { Component } from '@angular/core';
import { formatChartJSTooltip } from '@emma-helpers/emma-utils';
import { formatNumber } from '@emma-helpers/format.helper';
import { limitLabelSize } from '@emma-helpers/parse-chart-data';
import { DATE_FORMATS } from 'emma-common-ts';
import { ChartOptions, SerialData } from 'emma-common-ts/emma';

import Chart from 'chart.js';
import { EMMAChartBase } from './emma-chart-base';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const mUtil: any;

const styles = ['altgreen', 'altdarkblue', 'altblue', 'altviolet', 'altyellow', 'altsoftorange'];

type Options = Pick<
  ChartOptions,
  'showXAxe' | 'showYAxe' | 'monochrome' | 'colors' | 'keepZeros' | 'showLegend'
>;

interface DataSet {
  label?: string;
  backgroundColor?: string[];
  data: number[];
}

const defaultOptions: Options = {
  showXAxe: true,
  showYAxe: true,
  monochrome: true,
  colors: styles,
  keepZeros: false,
  showLegend: false,
};

@Component({
    selector: 'emma-barschart',
    templateUrl: './emma-chart.template.html',
    standalone: false
})
export class EMMABarsChartComponent extends EMMAChartBase {
  readonly MAX_LABELS_TO_FLEX = 15;
  protected data: SerialData = { labels: [], values: {} };
  protected override options: Options = defaultOptions;

  override init(): void {
    const el = document.getElementById(this.chartId) as HTMLCanvasElement;
    if (!el) {
      throw new Error('Chart not found: ' + this.chartId);
    }
    const ctx = el.getContext('2d');
    if (!ctx) {
      throw new Error('Chart without Context2D: ' + this.chartId);
    }
    const config = {
      type: 'bar',
      data: {
        labels: [],
        datasets: [],
      },
      options: { ...this.getOptions() },
    };
    this.chart = new Chart(ctx, config);
    this.chartLoad.emit(true);
    if (this.export) {
      this.export.subscribe(() => this.exportChart());
    }
  }

  override getStyle = (i = 0, forced = false): string =>
    this.options.monochrome && !forced
      ? this.options.colors[0]
      : this.options.colors[i % this.options.colors.length];

  private generateDatasets(): DataSet[] {
    if (this.data.stackedValues && this.data.stackLabels) {
      const { stackLabels, stackedValues, labels } = this.data;
      const barThickness = labels.length < this.MAX_LABELS_TO_FLEX ? 60 : 'flex';
      return stackLabels
        .filter((label) => stackedValues[label].some(Boolean))
        .map((label, i) => ({
          label,
          backgroundColor: mUtil.getColor(this.getStyle(i, true)),
          data: stackedValues[label],
          barThickness, //
        }));
    } else {
      const labels = this.data.labels;
      return [
        {
          label: this.data.label,
          backgroundColor: labels.map((__, i) => mUtil.getColor(this.getStyle(i))),
          data: labels.map((label) => this.data.values[label] || 0),
        },
      ];
    }
  }

  private filterEmptyBars(labels: string[], datasets: DataSet[]): { labels: string[]; datasets: DataSet[] } {
    const removedIndexes: number[] = [];
    const filteredLabels = labels.filter((_, i) => {
      const hasData = datasets.some((set) => Boolean(set.data[i]));
      if (!hasData) {
        removedIndexes.push(i);
      }
      return hasData;
    });

    const filteredDatasets = datasets.map((set) => ({
      ...set,
      data: set.data.filter((__, j) => !removedIndexes.includes(j)),
    }));

    return { labels: filteredLabels, datasets: filteredDatasets };
  }

  protected override _updateChart(): void {
    if (!this.chart) {
      return;
    }

    let labels = this.data.labels.slice(0);
    let datasets = this.generateDatasets();

    if (!this.options.keepZeros) {
      const filtered = this.filterEmptyBars(labels, datasets);
      labels = filtered.labels;
      datasets = filtered.datasets;
    }

    this.chart.data.labels = labels;
    this.chart.data.datasets = datasets;
    this.chart.options = { ...this.getOptions() };
    this.chart.update();
  }

  override update(data: SerialData | null, options: Partial<Options> = {}): void {
    if (data && data.error) {
      return this.error(data.error);
    } else if (!data || data.loading || !data.values) {
      return this.loading();
    }
    this.data = { ...data };
    this.loaded();
    this.options = {
      ...defaultOptions,
      monochrome: this.data.labels.length > this.options.colors.length,
      ...options,
    };
    this._updateChart();
  }

  getOptions = (): Chart.ChartOptions => {
    return {
      title: {
        display: false,
      },
      tooltips: {
        enabled: true,
        intersect: false,
        mode: 'nearest',
        xPadding: 10,
        yPadding: 10,
        caretPadding: 10,
        callbacks: {
          label: formatChartJSTooltip,
        },
      },
      legend: {
        display: this.options.showLegend,
        position: 'bottom',
        labels: {
          fontColor: '#00000',
          padding: 30,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        xAxes: [
          {
            display: this.options.showXAxe,
            gridLines: {
              display: false,
            },
            stacked: true,
            offset: true,
            ticks: {
              autoSkip: false,
              maxRotation: 90,
              minRotation: 0,
              callback: limitLabelSize,
              fontColor: '#00000',
            },
            time: {
              unit: 'day',
              displayFormats: {
                day: DATE_FORMATS.DAY_MONTH,
              },
            },
          },
        ],
        yAxes: [
          {
            display: this.options.showYAxe,
            stacked: true,
            offset: true,
            gridLines: {
              display: true,
            },
            ticks: {
              beginAtZero: true,
              fontColor: '#00000',
              callback: (value: number) => (value % 1 === 0 ? formatNumber(value) : undefined),
            },
          },
        ],
      },
      animation: this.export ? { duration: 0 } : { duration: 500, easing: 'easeOutQuart' },
    };
  };
}
